import heroImage from '@/assets/img/users-cases/ai-floor-plan-17-apartment-tours-hero.webp';
import workflowImage from '@/assets/img/users-cases/ai-floor-plan-17-apartment-tours-workflow.webp';
import reviewImage from '@/assets/img/users-cases/ai-floor-plan-17-apartment-tours-review.webp';
import tourImage from '@/assets/img/users-cases/ai-floor-plan-17-apartment-tours-360-tour.webp';

export default {
  slug: 'ai-floor-plan-17-apartment-tours',
  order: 1,
  title: 'AI 2D-to-3D floor plan conversion. Case: 17 360° tours for a developer in 3 days',
  subtitle: 'AI-generated apartment tours, checked by our team and ready to help buyers explore their future homes.',
  metaTitle: 'AI floor plan case study: 17 apartment tours in 3 days',
  metaDescription: 'See how Getfloorplan turned 17 apartment floor plans into furnished 360° tours for a developer in 3 days, including team review and revisions.',
  tags: [],

  images: {
    hero: heroImage,
  },
  heroImageAlt: '2D floor plan over a furnished apartment interior',

  heroText: [
    'AI-generated apartment tours, checked by our team and ready to help buyers explore their future homes.',
  ],

  sections: [
    {
      title: 'The challenge: presenting 17 apartments before construction was complete',
      wideText: true,
      paragraphs: [
        'A residential developer had 3 working days before the project website launch. The team had 17 floor plans, but no interactive interiors to show buyers.',
        'With construction underway, in-person viewings were not available. The developer needed 17 furnished 360° tours in a consistent style to help buyers understand the spaces.',
      ],
    },
    {
      title: 'From floor plans to AI-generated 3D scenes',
      image: workflowImage,
      imageAlt: '2D floor plan transformed into a furnished 3D floor plan',
      reverse: false,
      paragraphs: [
        '<strong>1. Preparing the source floor plans</strong><br>We checked the supplied layouts and agreed on the interior style, deliverables and review schedule.',
        '<strong>2. Creating furnished apartment scenes with AI</strong><br>Getfloorplan AI turned the 17 floor plans into furnished 3D scenes, following each source layout and the agreed interior style.',
      ],
    },
    {
      title: 'Team review and developer feedback',
      image: reviewImage,
      imageAlt: 'Before and after 3D floor plan review showing the corrected sofa position',
      reverse: true,
      paragraphs: [
        '<strong>3. Checking the 3D visualizations</strong><br>The team checked rooms, doors, windows and furniture placement against the drawings, correcting identified issues before sharing previews.',
        '<strong>4. Completing agreed revisions</strong><br>The developer returned consolidated feedback. We completed the agreed changes and checked the final tours.',
        '<strong>5. Meeting the deadline without skipping review</strong><br>All 17 tours needed production, review and approval within three working days.',
        'Agreeing on style, scheduling reviews, and consolidating feedback kept the series moving without apartment-by-apartment approvals.',
      ],
    },
    {
      title: 'The result: 17 apartment tours in 3 working days',
      reverse: false,
      dividerBelow: true,
      titleParagraphs: [
        'The developer received all 17 tours before the website launch. The turnaround included AI production, team review and agreed revisions, starting with approval of the complete source files and interior requirements.',
      ],
      paragraphs: [
        'See how a source floor plan became a furnished space buyers can explore.',
        'The interior illustrates a possible use of the space. Included finishes and furnishings depend on the developer’s offer.',
      ],
      cta: {
        text: 'Explore the apartment tour',
        href: 'https://base.getfloorplan.com/?crmPlanId=490032',
      },
    },
    {
      title: '360° apartment tours for real estate marketing',
      image: tourImage,
      imageAlt: 'One-bedroom apartment 360-degree tour displayed on a laptop',
      reverse: true,
      paragraphs: [
        'Show interiors alongside floor plans on apartment pages, during sales presentations or in remote conversations. Buyers can explore room connections and proposed furniture arrangements.',
      ],
      subsectionTitle: 'Have floor plans and a launch deadline?',
      subsectionParagraphs: [
        'Send them to Getfloorplan to discuss your project’s scope and delivery schedule.',
      ],
      cta: {
        text: 'Discuss your project',
        href: '/pricing',
      },
    },
  ],

  related: [
    '3D-tour-american-flat',
    'residential-property-marketing-campaigns',
    'smart-space-apartment',
    'boho-harmony-apartment',
  ],
};
